/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import apiClient from './client';
import {
  APIResponse,
  UserAuthenticationResult,
  OwnerDTO,
  UserByOwnerDTO,
  DriverLicenseDTO
} from '@/types';
import { isSameEmail } from '@/utils';

interface AegisUser extends UserAuthenticationResult {}

// ── Duplicate-driver merge (mirrors backend UserMergeService) ────────────────
// One side of a merge: who they are and how much data hangs off them, so the
// operator can tell the two records apart before choosing which one survives.
export interface MergeUserSummary {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  created?: string;
  bookings: number;
  tollCharges: number;
  cards: number;
  cardLabel?: string;
  licenseNumber?: string;
  roles: string[];
  isOwner: boolean;
  isAdmin: boolean;
  /** Auto-created from platform data (synthetic @none.com address). */
  isPlaceholder: boolean;
}

export interface MergeStep {
  table: string;
  column: string;
  moved: number;
  dropped: number;
  note?: string;
}

export interface MergeReport {
  dryRun: boolean;
  source: MergeUserSummary;
  target: MergeUserSummary;
  steps: MergeStep[];
  totalMoved: number;
  totalDropped: number;
  adoptedFields: string[];
  regatedBookings: string[];
  leftovers: string[];
  warnings: string[];
}

export interface MergeOptions {
  sourceUserId: string;
  targetUserId: string;
  /** Which side's saved card survives when both have one. */
  keepCardFrom?: 'target' | 'source';
  /** Which side's driver licence survives when both have one. */
  keepLicenseFrom?: 'target' | 'source';
}

// Server-paged driver list envelope (mirrors backend PagedUsersDTO).
export interface PagedUsers {
  items: UserByOwnerDTO[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** How a verification code reaches the user. */
export type MfaChannel = 'email' | 'sms';

/** One owner's second-factor setting, as an administrator sees it. Never shown to the owner. */
export interface MfaSetting {
  enabled: boolean;
  channel: MfaChannel;
  /** Whether SMS is even selectable — without a phone on file it would lock the owner out. */
  hasPhone: boolean;
}

// Generic server-paged envelope (mirrors backend PagedResult<T>).
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  zipCode: string;
  addressLine1: string;
  addressLine2: string;
  state: string;
  city?: string;
  country?: string;
  dateOfBirth?: string;
  licenseNumber?: string;
  expirationDate?: string;
}

export interface UpdateOwnerProfileRequest {
  firstName: string;
  lastName: string;
  middleName?: string;
  company?: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
}

interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  isOwner?: boolean;
  isRenter?: boolean;
  ownerId?: string;
  smsConsent?: boolean;
}

interface CreateUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface CommunicationPreferences {
  smsConsent: boolean;
  smsConsentDate?: string;
  preferredChannel?: string;
  phoneVerified?: boolean;
}

export const usersApi = {
  // Resolve multiple user IDs to names (batch)
  resolveUserNames: async (userIds: string[]): Promise<Record<string, string>> => {
    const unique = [...new Set(userIds.filter(Boolean))];
    if (unique.length === 0) return {};
    try {
      const response = await apiClient.get<{ id: string; firstName: string; lastName: string; email: string }[]>(
        `/Users/Aegis/${unique.join(',')}`
      );
      const users = response.data;
      const map: Record<string, string> = {};
      (Array.isArray(users) ? users : []).forEach((u) => {
        map[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id;
      });
      return map;
    } catch {
      return {};
    }
  },

  // Admin: change password for any user.
  //
  // Throws when the API answers 200 with result:false. That reply means the password was NOT
  // written — a blank userId, a blank password, or an account the server could not find — and
  // returning false here made the caller's mutation succeed and show "password changed" over a
  // password that never left the browser. Callers rely on a rejected promise to show the failure.
  adminChangePassword: async (userId: string, newPassword: string): Promise<boolean> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      '/Users/adminchangepassword',
      { userId, newPassword }
    );
    if (response.data.result !== true) {
      throw new Error(response.data.message || 'Password was not changed');
    }
    return true;
  },

  // Admin: clear a sign-in lock so the user can retry immediately.
  //
  // The lock is temporary and expires on its own; this is for the support case where waiting is
  // not an option. It resets the failure counters only — it does not change the password.
  unlockUser: async (userId: string): Promise<boolean> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      `/Users/admin/unlock/${userId}`
    );
    return response.data.result ?? false;
  },

  // Admin: read one owner's second-factor setting.
  //
  // There is no owner-facing counterpart on purpose. An owner is not shown this control at all —
  // the point of an admin-controlled second factor is that the account it protects cannot switch
  // it off, and even a read would tell an attacker holding the password whether one is in the way.
  getMfa: async (userId: string): Promise<MfaSetting> => {
    const response = await apiClient.get<APIResponse<MfaSetting>>(`/Users/admin/mfa/${userId}`);
    return response.data.result ?? { enabled: false, channel: 'email', hasPhone: false };
  },

  // Admin: turn one owner's second factor on or off and pick how the code reaches them.
  //
  // This does NOT govern administrators. An admin's own second factor comes from the
  // Mfa__AdminEnabled App Service setting and is deliberately unreachable from the UI: an admin who
  // could clear their own requirement from the console they already control would have none.
  setMfa: async (userId: string, enabled: boolean, channel: MfaChannel): Promise<boolean> => {
    const response = await apiClient.put<APIResponse<boolean>>(
      `/Users/admin/mfa/${userId}`,
      { enabled, channel }
    );
    return response.data.result ?? false;
  },

  // Get user by email.
  // includeAdditional widens the lookup to the user's ADDITIONAL e-mail addresses when the primary
  // one misses — opt-in, because a shared secondary address can resolve to the wrong person.
  getUserByEmail: async (email: string, includeAdditional = false): Promise<AegisUser | null> => {
    const response = await apiClient.get<APIResponse<AegisUser>>(
      `/Users/GetByEmail/${email}`,
      { params: includeAdditional ? { includeAdditional: true } : undefined }
    );
    return response.data.result || null;
  },

  // Get user by phone (returns array - multiple users can share a phone number).
  // includeAdditional also matches ADDITIONAL numbers; results are de-duplicated by user id.
  getUserByPhone: async (phone: string, includeAdditional = false): Promise<AegisUser[]> => {
    const response = await apiClient.get<APIResponse<AegisUser[]>>(
      `/Users/GetByPhone/${phone}`,
      { params: includeAdditional ? { includeAdditional: true } : undefined }
    );
    return response.data.result || [];
  },

  // Get all owners (admin only) — full array, kept for the dashboard + Owners-page Excel export.
  getAllOwners: async (): Promise<OwnerDTO[]> => {
    const response = await apiClient.get<APIResponse<OwnerDTO[]>>(
      '/Users/GetAllOwners'
    );
    return response.data.result || [];
  },

  // Server-paged owners (admin Owners page): page/pageSize + name/email/id search.
  // kinds: any mix of 'partner' | 'admin' | 'other' (neither); empty/undefined = everyone.
  getAllOwnersPaged: async (
    opts?: { page?: number; pageSize?: number; search?: string; kinds?: string[] }
  ): Promise<PagedResult<OwnerDTO>> => {
    const pageSize = opts?.pageSize ?? 25;
    const params = new URLSearchParams();
    if (opts?.page) params.append('page', String(opts.page));
    if (opts?.pageSize) params.append('pageSize', String(opts.pageSize));
    if (opts?.search?.trim()) params.append('search', opts.search.trim());
    if (opts?.kinds?.length) params.append('kinds', opts.kinds.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<APIResponse<PagedResult<OwnerDTO>>>(
      `/Users/GetAllOwnersPaged${query}`
    );
    return response.data.result || { items: [], totalCount: 0, page: opts?.page ?? 1, pageSize };
  },

  // Server-paged owner driver list. `search` matches the whole owner's driver set in the DB
  // (name/email/phone), not just the returned page. Used by the paginated Users page.
  // includeAdditionalContacts also searches the drivers' ADDITIONAL phones / e-mails (opt-in).
  getOwnerUsersPaged: async (
    ownerId: string,
    opts?: { dateFrom?: string; dateTo?: string; page?: number; pageSize?: number; search?: string; emailConfirmed?: boolean; onlyFailed?: boolean; includeAdditionalContacts?: boolean }
  ): Promise<PagedUsers> => {
    const pageSize = opts?.pageSize ?? 25;
    if (!ownerId) {
      console.error('getOwnerUsersPaged called without ownerId');
      return { items: [], totalCount: 0, page: 1, pageSize };
    }
    const params = new URLSearchParams();
    if (opts?.dateFrom) params.append('dateFrom', opts.dateFrom);
    if (opts?.dateTo) params.append('dateTo', opts.dateTo);
    if (opts?.page) params.append('page', String(opts.page));
    if (opts?.pageSize) params.append('pageSize', String(opts.pageSize));
    if (opts?.search?.trim()) params.append('search', opts.search.trim());
    if (opts?.emailConfirmed !== undefined) params.append('emailConfirmed', String(opts.emailConfirmed));
    if (opts?.onlyFailed) params.append('onlyFailed', 'true');
    if (opts?.includeAdditionalContacts) params.append('includeAdditionalContacts', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<APIResponse<PagedUsers>>(
      `/Users/get-owner-users/${ownerId}${query}`
    );
    return response.data.result || { items: [], totalCount: 0, page: opts?.page ?? 1, pageSize };
  },

  // Get users by owner as a flat array (all drivers, up to the backend cap). Kept for callers that
  // need the full set for a picker/stat (booking modal, dashboard) rather than a page.
  getUsersByOwner: async (ownerId: string, dateFrom?: string, dateTo?: string): Promise<UserByOwnerDTO[]> => {
    if (!ownerId) {
      console.error('getUsersByOwner called without ownerId');
      return [];
    }
    const paged = await usersApi.getOwnerUsersPaged(ownerId, { dateFrom, dateTo, page: 1, pageSize: 1000 });
    return paged.items;
  },

  // Global pool of new unbooked drivers (registered recently, never booked). Server-side
  // paginated + searched against the whole DB pool.
  getUnbookedDrivers: async (
    opts?: { page?: number; pageSize?: number; search?: string; emailConfirmed?: boolean; onlyFailed?: boolean; includeAdditionalContacts?: boolean }
  ): Promise<PagedUsers> => {
    const pageSize = opts?.pageSize ?? 25;
    const params = new URLSearchParams();
    if (opts?.page) params.append('page', String(opts.page));
    if (opts?.pageSize) params.append('pageSize', String(opts.pageSize));
    if (opts?.search?.trim()) params.append('search', opts.search.trim());
    if (opts?.emailConfirmed !== undefined) params.append('emailConfirmed', String(opts.emailConfirmed));
    if (opts?.onlyFailed) params.append('onlyFailed', 'true');
    if (opts?.includeAdditionalContacts) params.append('includeAdditionalContacts', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<APIResponse<PagedUsers>>(
      `/Users/get-unbooked-drivers${query}`
    );
    return response.data.result || { items: [], totalCount: 0, page: opts?.page ?? 1, pageSize };
  },

  // Get driver license
  getDriverLicense: async (userId: string): Promise<DriverLicenseDTO | null> => {
    const response = await apiClient.get<APIResponse<DriverLicenseDTO>>(
      `/Users/get-driver-license/${userId}`
    );
    return response.data.result || null;
  },

  // Upload driver license image
  uploadDriverLicenseImage: async (
    userId: string,
    file: File,
    side: 'front' | 'back'
  ): Promise<APIResponse<string>> => {
    const endpoint = side === 'front'
      ? '/RegistrationLinkRequest/upload-driver-license-front'
      : '/RegistrationLinkRequest/upload-driver-license-back';

    const formData = new FormData();
    formData.append('UserId', userId);
    formData.append('ImageFile', file);

    const response = await apiClient.post<APIResponse<string>>(
      endpoint,
      formData,
      {
        transformRequest: [(data: any, headers: any) => {
          delete headers['Content-Type'];
          return data;
        }],
        timeout: 60000,
      }
    );
    return response.data;
  },

  // Update user details
  updateUser: async (userId: string, email: string, data: UpdateUserRequest): Promise<boolean> => {
    // The address is deliberately NOT in the URL. The backend resolves it from userId, and a path
    // segment containing "+" (plus-addressing) is rejected by IIS with a 404 before it reaches the
    // application — which looked like the save silently doing nothing.
    const url = `/Users/UpdateUserByParam/${userId}`;

    // If email changed, update it first. The backend rejects a duplicate address
    // with 409 (see UsersController.UpdateProfile2), so this call throws before any
    // of the profile steps below run — callers surface the conflict to the user.
    if (data.email && !isSameEmail(data.email, email)) {
      await apiClient.post(url, { email: data.email, updateStep: 6 }); // 6 = Email step
    }

    // CommonInfo (0)
    await apiClient.post(url, {
      firstName: data.firstName,
      lastName: data.lastName,
      driverLicenseId: data.licenseNumber || undefined,
      birthDate: data.dateOfBirth || undefined,
      driverLicenseExpirationDate: data.expirationDate || undefined,
      updateStep: 0,
    });

    // Location (1)
    await apiClient.post(url, {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      zipCode: data.zipCode,
      state: data.state,
      city: data.city || undefined,
      country: data.country || undefined,
      updateStep: 1,
    });

    // Phone (2)
    await apiClient.post(url, {
      phone: data.phone,
      updateStep: 2,
    });

    return true;
  },

  // Update an owner's own profile. Mirrors the owner-registration wizard fields
  // (company + personal info + primary location) rather than the driver-license
  // shape used by updateUser. Company/MiddleName are persisted in the CommonInfo
  // step (see UserService.UpdatePerson). A blank company reads as "not supplied"
  // there — the driver-license and mobile paths share that step and carry none —
  // so clearing one is requested explicitly with clearCompany.
  updateOwnerProfile: async (
    userId: string,
    email: string,
    data: UpdateOwnerProfileRequest
  ): Promise<boolean> => {
    // No address in the URL — see the note in updateUser.
    const url = `/Users/UpdateUserByParam/${userId}`;

    // Email is the login key — change it first so a 409 (duplicate) throws before
    // the remaining steps run (backend UsersController.UpdateProfile2).
    if (data.email && !isSameEmail(data.email, email)) {
      await apiClient.post(url, { email: data.email, updateStep: 6 }); // 6 = Email step
    }

    // CommonInfo (0) — name, company, date of birth
    const company = (data.company ?? '').trim();
    await apiClient.post(url, {
      firstName: data.firstName,
      lastName: data.lastName,
      middleName: data.middleName || undefined,
      company: company || undefined,
      isCompany: !!company,
      clearCompany: !company,
      birthDate: data.dateOfBirth || undefined,
      updateStep: 0,
    });

    // Location (1) — primary address
    await apiClient.post(url, {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      zipCode: data.zipCode,
      state: data.state,
      city: data.city || undefined,
      country: data.country || undefined,
      updateStep: 1,
    });

    // Phone (2)
    await apiClient.post(url, {
      phone: data.phone,
      updateStep: 2,
    });

    return true;
  },

  // Update only Users.Phone. Email is login-key and is NEVER changed by this call.
  updateDriverPhone: async (userId: string, phone: string): Promise<void> => {
    await apiClient.post(`/Users/UpdateDriverPhone/${userId}`, { phone });
  },

  // Get user communication preferences (SMS consent etc.)
  getCommunicationPreferences: async (userId: string): Promise<CommunicationPreferences> => {
    const response = await apiClient.get<APIResponse<CommunicationPreferences>>(
      `/UserCommunicationPreferences/${userId}`
    );
    return response.data.result || { smsConsent: true };
  },

  // Update user communication preferences
  updateCommunicationPreferences: async (userId: string, smsConsent: boolean): Promise<void> => {
    await apiClient.put(`/UserCommunicationPreferences/${userId}`, {
      smsConsent,
    });
  },

  // Delete user and all related records regardless of role (admin only)
  /**
   * Closes the CALLER'S OWN account. Not the same call as deleteUser above: that one is the admin
   * hard delete, this one marks the account deleted and leaves the financial record standing, which
   * is what owner.myeztoll.com/delete-account promises in public and what Google Play requires the
   * app to offer from the inside.
   *
   * Every later request 401s once this returns, and the app logs out on any 401 — so treat the
   * response as final and sign out immediately rather than refetching anything.
   */
  closeMyAccount: async (): Promise<boolean> => {
    await apiClient.post('/Users/close-my-account');
    return true;
  },

  deleteUser: async (userId: string): Promise<boolean> => {
    await apiClient.delete(`/Users/delete-user/${userId}`);
    return true;
  },

  // ── Duplicate-driver merge (admin only) ─────────────────────────────────────
  // One driver as the merge screen shows them — needed for the record the operator
  // started from, which never appears in its own candidate list.
  getMergeSummary: async (userId: string): Promise<MergeUserSummary | null> => {
    const response = await apiClient.get<APIResponse<MergeUserSummary>>(`/Users/merge/summary/${userId}`);
    return response.data.result || null;
  },

  // Drivers that look like the same person as `userId`; `search` overrides the
  // automatic name/phone/licence matching with a manual lookup.
  getMergeCandidates: async (userId: string, search?: string): Promise<MergeUserSummary[]> => {
    const response = await apiClient.get<APIResponse<MergeUserSummary[]>>(
      `/Users/merge/candidates/${userId}`,
      { params: search ? { search } : undefined }
    );
    return response.data.result || [];
  },

  // What the merge WOULD do — the server runs it for real and rolls back, so the
  // counts are exact rather than estimated. Nothing is written.
  previewMerge: async (options: MergeOptions): Promise<MergeReport> => {
    const response = await apiClient.post<APIResponse<MergeReport>>('/Users/merge/preview', options);
    return response.data.result;
  },

  // Commit the merge. The source record is gone afterwards.
  mergeUsers: async (options: MergeOptions): Promise<MergeReport> => {
    const response = await apiClient.post<APIResponse<MergeReport>>('/Users/merge', options);
    return response.data.result;
  },

  // Create a new user (driver/renter)
  createUser: async (data: CreateUserRequest): Promise<APIResponse<CreateUserResponse>> => {
    const requestBody: any = {
      email: data.email,
      password: 'TempPass123!', // Temporary password - user will need to reset
      firstName: data.firstName,
      lastName: data.lastName,
      middleName: '',
      companyId: null,
      company: null,
      isCompany: false,
      zipCode: '',
      phone: data.phone,
      birthDate: new Date().toISOString(),
      city: '',
      stateId: '',
      addressLine1: '',
      addressLine2: '',
      receiveNotification: true,
      adsSubscriptions: false,
      isOwnerSelected: data.isOwner ?? false,
      isRenterSelected: data.isRenter ?? true,
      confirmEighteenYearOld: true,
      smsConsent: data.smsConsent ?? true,
      // skipStandardAuthEmail: true, // Email is sent after agreement creation, not on signup
    };

    // Add OwnerId if provided
    if (data.ownerId) {
      requestBody.ownerId = data.ownerId;
    }

    const response = await apiClient.post<APIResponse<CreateUserResponse>>(
      '/UserAuth/signup',
      requestBody
    );
    return response.data;
  },
};
